import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { NavBar, Tag, Tabs, Loading, Empty, ActionSheet, Button } from 'react-vant'
import { ArrowLeft, ArrowDown, ChartTrendingO, Warning } from '@react-vant/icons'
import request from '../../api/request'

export default function FitnessPortfolio() {
    const navigate = useNavigate()
    const location = useLocation()
    
    // Parse student_id from query if present
    const queryParams = new URLSearchParams(location.search)
    const urlStudentId = queryParams.get('student_id')

    const [loading, setLoading] = useState(true)
    const [children, setChildren] = useState([])
    const [selectedChild, setSelectedChild] = useState(null)
    const [portfolioData, setPortfolioData] = useState(null)
    const [showChildPicker, setShowChildPicker] = useState(false)
    const [activeTab, setActiveTab] = useState('assessment') // 'assessment', 'trends', 'items'

    // 1. Fetch children or student portfolio on mount
    useEffect(() => {
        const fetchInitialData = async () => {
            setLoading(true)
            const targetStudentId = queryParams.get('student_id') || queryParams.get('studentId')
            
            if (targetStudentId) {
                try {
                    const res = await request.get(`/sports/tests/portfolio/student/${targetStudentId}`)
                    if (res && res.student) {
                        setSelectedChild(res.student)
                        setPortfolioData(res)
                    } else {
                        setPortfolioData(null)
                    }
                } catch (e) {
                    console.error('获取指定学生成长档案失败:', e)
                    setPortfolioData(null)
                } finally {
                    setLoading(false)
                }
                return
            }

            try {
                const res = await request.get('/academic/students/m/children/')
                if (res.success && res.data && res.data.length > 0) {
                    setChildren(res.data)
                    setSelectedChild(res.data[0])
                } else {
                    setChildren([])
                }
            } catch (e) {
                console.error('获取关联学生失败:', e)
                setChildren([])
            } finally {
                setLoading(false)
            }
        }
        fetchInitialData()
    }, [location.search])

    // 2. Fetch portfolio data when selected child changes (only in multi-child parent mode)
    useEffect(() => {
        const targetStudentId = queryParams.get('student_id') || queryParams.get('studentId')
        if (targetStudentId || !selectedChild) return

        const fetchPortfolio = async () => {
            setLoading(true)
            try {
                const res = await request.get(`/sports/tests/portfolio/student/${selectedChild.id}`)
                if (res && res.student) {
                    setPortfolioData(res)
                } else {
                    setPortfolioData(null)
                }
            } catch (e) {
                console.error('获取成长档案失败:', e)
                setPortfolioData(null)
            } finally {
                setLoading(false)
            }
        }
        fetchPortfolio()
    }, [selectedChild])

    // Switch child action sheet handlers
    const pickerActions = children.map(child => ({
        name: child.real_name,
        subname: child.class_name || '未分配班级',
        value: child.id,
        color: selectedChild?.id === child.id ? 'var(--primary-green)' : undefined,
    }))

    const handleSelectChild = (action) => {
        const child = children.find(c => c.id === action.value)
        setSelectedChild(child)
        setShowChildPicker(false)
    }

    // --- SVG Radar Chart Component ---
    const renderRadarChart = (radarData) => {
        if (!radarData || radarData.length === 0) return <Empty description="暂无雷达图指标" />

        const cx = 160;
        const cy = 130;
        const r = 85;
        const totalPoints = radarData.length;

        // Angle helper
        const getAngle = (i) => -Math.PI / 2 + (2 * Math.PI * i) / totalPoints;

        // Draw web grid pentagons
        const levels = [20, 40, 60, 80, 100];
        const gridPolygons = levels.map(level => {
            const points = [];
            for (let i = 0; i < totalPoints; i++) {
                const angle = getAngle(i);
                const x = cx + r * (level / 100) * Math.cos(angle);
                const y = cy + r * (level / 100) * Math.sin(angle);
                points.push(`${x},${y}`);
            }
            return points.join(' ');
        });

        // Draw axis lines
        const axisLines = [];
        for (let i = 0; i < totalPoints; i++) {
            const angle = getAngle(i);
            const x = cx + r * Math.cos(angle);
            const y = cy + r * Math.sin(angle);
            axisLines.push({ x1: cx, y1: cy, x2: x, y2: y });
        }

        // Draw student score polygon
        const scorePointsArray = [];
        radarData.forEach((item, i) => {
            const angle = getAngle(i);
            const ratio = Math.max(10, Math.min(100, item.score)) / 100; // clamp between 10% and 100%
            const x = cx + r * ratio * Math.cos(angle);
            const y = cy + r * ratio * Math.sin(angle);
            scorePointsArray.push(`${x},${y}`);
        });
        const scorePolygonPoints = scorePointsArray.join(' ');

        return (
            <div style={{ display: 'flex', justifyContent: 'center', margin: '16px 0', background: '#fff', borderRadius: 16, padding: '16px 8px' }}>
                <svg width="100%" height="250" viewBox="0 0 320 250" style={{ maxWidth: 350 }}>
                    {/* Background concentric pentagons */}
                    {gridPolygons.map((pts, idx) => (
                        <polygon
                            key={idx}
                            points={pts}
                            fill="none"
                            stroke="#e2e8f0"
                            strokeWidth="1"
                        />
                    ))}

                    {/* Concentric grid label ticks */}
                    {levels.map((level, idx) => {
                        const angle = getAngle(0);
                        const y = cy + r * (level / 100) * Math.sin(angle);
                        return (
                            <text
                                key={idx}
                                x={cx + 4}
                                y={y + 3}
                                fontSize="9"
                                fill="#94a3b8"
                                fontWeight="bold"
                            >
                                {level}
                            </text>
                        );
                    })}

                    {/* Axis Lines */}
                    {axisLines.map((line, idx) => (
                        <line
                            key={idx}
                            x1={line.x1}
                            y1={line.y1}
                            x2={line.x2}
                            y2={line.y2}
                            stroke="#f1f5f9"
                            strokeWidth="1.5"
                        />
                    ))}

                    {/* The score polygon */}
                    <polygon
                        points={scorePolygonPoints}
                        fill="rgba(0, 181, 120, 0.18)"
                        stroke="#00b578"
                        strokeWidth="2.5"
                        strokeLinejoin="round"
                    />

                    {/* Data Points */}
                    {radarData.map((item, i) => {
                        const angle = getAngle(i);
                        const ratio = Math.max(10, Math.min(100, item.score)) / 100;
                        const x = cx + r * ratio * Math.cos(angle);
                        const y = cy + r * ratio * Math.sin(angle);
                        return (
                            <circle
                                key={i}
                                cx={x}
                                cy={y}
                                r="4.5"
                                fill="#00b578"
                                stroke="#fff"
                                strokeWidth="1.5"
                                style={{ filter: 'drop-shadow(0 2px 4px rgba(0, 181, 120, 0.2))' }}
                            />
                        );
                    })}

                    {/* Labels with dynamic text anchoring */}
                    {radarData.map((item, i) => {
                        const angle = getAngle(i);
                        const offset = 16;
                        const x = cx + (r + offset) * Math.cos(angle);
                        const y = cy + (r + offset) * Math.sin(angle);

                        // Align anchors based on angle position
                        let textAnchor = 'middle';
                        if (Math.cos(angle) > 0.1) textAnchor = 'start';
                        else if (Math.cos(angle) < -0.1) textAnchor = 'end';

                        let dy = '0.35em';
                        if (Math.sin(angle) < -0.9) dy = '-0.2em';
                        else if (Math.sin(angle) > 0.9) dy = '1em';

                        // Shorten labels for mobile
                        let label = item.item;
                        if (label.includes('(')) label = label.split('(')[0];

                        return (
                            <g key={i}>
                                <text
                                    x={x}
                                    y={y}
                                    dy={dy}
                                    fontSize="10"
                                    fontWeight="700"
                                    fill="#334155"
                                    textAnchor={textAnchor}
                                >
                                    {label}
                                </text>
                                <text
                                    x={x}
                                    y={y}
                                    dy={dy === '1em' ? '2.1em' : dy === '-0.2em' ? '0.85em' : '1.35em'}
                                    fontSize="10"
                                    fontWeight="bold"
                                    fill="#00b578"
                                    textAnchor={textAnchor}
                                >
                                    {item.score}分
                                </text>
                            </g>
                        );
                    })}
                </svg>
            </div>
        );
    }

    // --- SVG Line Chart for History Trends ---
    const renderTrendChart = (title, data, valueKey, labelKey, color, unit = '') => {
        if (!data || data.length === 0) return <Empty description={`暂无${title}趋势数据`} />

        const width = 300;
        const height = 130;
        const padding = { top: 20, right: 20, bottom: 25, left: 35 };

        const chartW = width - padding.left - padding.right;
        const chartH = height - padding.top - padding.bottom;

        // Find min and max
        const scores = data.map(d => Number(d[valueKey] || 0));
        let maxVal = Math.max(...scores, 100);
        let minVal = Math.min(...scores, 0);
        
        // Add padding to min/max
        const valRange = maxVal - minVal || 10;
        maxVal += valRange * 0.1;
        minVal = Math.max(0, minVal - valRange * 0.1);

        const getX = (index) => {
            if (data.length === 1) {
                return padding.left + chartW / 2;
            }
            return padding.left + (index / (data.length - 1)) * chartW;
        };
        const getY = (val) => padding.top + chartH - ((val - minVal) / (maxVal - minVal)) * chartH;

        // Path generator
        const points = data.map((d, idx) => ({
            x: getX(idx),
            y: getY(Number(d[valueKey] || 0)),
            val: Number(d[valueKey] || 0),
            label: d[labelKey] || ''
        }));

        let pathD = '';
        if (points.length > 0) {
            pathD = `M ${points[0].x} ${points[0].y} ` + points.slice(1).map(p => `L ${p.x} ${p.y}`).join(' ');
        }

        return (
            <div style={{ background: '#fff', borderRadius: 16, padding: '16px 12px', marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 12 }}>{title}</div>
                <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`}>
                    {/* Y Axis Grid Lines */}
                    {[minVal, minVal + (maxVal - minVal) / 2, maxVal].map((val, idx) => {
                        const y = getY(val);
                        return (
                            <g key={idx}>
                                <line
                                    x1={padding.left}
                                    y1={y}
                                    x2={width - padding.right}
                                    y2={y}
                                    stroke="#f1f5f9"
                                    strokeWidth="1"
                                />
                                <text
                                    x={padding.left - 6}
                                    y={y + 3}
                                    fontSize="9"
                                    fill="#94a3b8"
                                    textAnchor="end"
                                >
                                    {Math.round(val)}{unit}
                                </text>
                            </g>
                        );
                    })}

                    {/* Trend Line */}
                    {pathD && (
                        <path
                            d={pathD}
                            fill="none"
                            stroke={color}
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    )}

                    {/* Points & Labels */}
                    {points.map((p, idx) => (
                        <g key={idx}>
                            <circle
                                cx={p.x}
                                cy={p.y}
                                r="4.5"
                                fill={color}
                                stroke="#fff"
                                strokeWidth="1.5"
                            />
                            <text
                                x={p.x}
                                y={p.y - 8}
                                fontSize="9.5"
                                fontWeight="bold"
                                fill={color}
                                textAnchor="middle"
                            >
                                {p.val}
                            </text>
                            <text
                                x={p.x}
                                y={height - 8}
                                fontSize="9"
                                fill="#64748b"
                                textAnchor="middle"
                            >
                                {p.label.replace('学年', '')}
                            </text>
                        </g>
                    ))}
                </svg>
            </div>
        );
    }

    // Render timeline logs
    const renderTimeline = (logs) => {
        if (!logs || logs.length === 0) return <Empty description="暂无历史发展轨迹" />
        return (
            <div style={{ background: '#fff', borderRadius: 16, padding: '16px' }}>
                {logs.map((log, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: 12, position: 'relative', paddingBottom: idx < logs.length - 1 ? 20 : 4 }}>
                        {/* Line connector */}
                        {idx < logs.length - 1 && (
                            <div style={{
                                position: 'absolute', top: 12, left: 16, bottom: 0,
                                width: 2, background: '#e2e8f0'
                            }} />
                        )}
                        {/* Badge circle */}
                        <div style={{
                            width: 34, height: 34, borderRadius: '50%',
                            background: 'linear-gradient(135deg, #e8f6f0, #d1fae5)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 12, zIndex: 2
                        }}>🏃</div>
                        {/* Info details */}
                        <div style={{ flex: 1, paddingTop: 3 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                                    {log.value} {log.unit || ''}
                                </span>
                                <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>
                                    {log.academic_year.replace('学年', '')}
                                </span>
                            </div>
                            <div style={{ fontSize: 11, color: '#64748b', marginTop: 3 }}>
                                测试时间：{log.date}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (loading && !portfolioData) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f5f8f6' }}>
                <Loading type="spinner" color="var(--primary-green)" vertical>正在生成成长档案...</Loading>
            </div>
        );
    }

    const latest = portfolioData?.latest || {}
    const trends = portfolioData?.trends || {}

    return (
        <div style={{ background: '#f5f8f6', minHeight: '100vh', paddingBottom: 30 }}>
            {/* NavBar */}
            {!window.ReactNativeWebView && (
                <NavBar
                    title="健康成长档案"
                    leftArrow={<ArrowLeft />}
                    onClickLeft={() => navigate(-1)}
                    fixed
                    placeholder
                />
            )}

            {/* Student Switcher Banner */}
            <div style={{ background: 'linear-gradient(135deg, #055e43 0%, #076f51 50%, #0c8c68 100%)', padding: '16px', color: '#fff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 18, fontWeight: 800 }}>{selectedChild?.real_name || selectedChild?.name}</span>
                            <Tag style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff' }}>
                                {selectedChild?.gender === 'male' || selectedChild?.gender === '男' ? '男' : '女'}
                            </Tag>
                        </div>
                        <div style={{ fontSize: 12, opacity: 0.85, marginTop: 4 }}>
                            {selectedChild?.class_name || '未分配班级'}
                        </div>
                    </div>

                    {children.length > 1 && (
                        <Button
                            size="small"
                            onClick={() => setShowChildPicker(true)}
                            style={{
                                background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff',
                                borderRadius: 14, height: 28, fontSize: 12, fontWeight: 600
                            }}
                            icon={<ArrowDown style={{ fontSize: 10 }} />}
                        >
                            切换学生
                        </Button>
                    )}
                </div>
            </div>

            {/* Tabs Selector */}
            <div style={{ margin: '12px 14px' }}>
                <Tabs active={activeTab} onChange={setActiveTab} color="#056b4f">
                    <Tabs.TabPane name="assessment" title="体质评估" />
                    <Tabs.TabPane name="trends" title="成长趋势" />
                    <Tabs.TabPane name="items" title="分项体能" />
                </Tabs>
            </div>

            {/* Loading Overlay */}
            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
                    <Loading type="spinner" color="#056b4f" />
                </div>
            ) : portfolioData ? (
                <div style={{ padding: '0 14px' }}>
                    {/* TAB 1: Evaluation */}
                    {activeTab === 'assessment' && (
                        <>
                            {/* Five Dimension Radar */}
                            {renderRadarChart(latest.radar)}

                            {/* Warnings Alerts */}
                            {latest.warnings && latest.warnings.length > 0 && (
                                <div style={{ background: '#fffbeb', border: '1px solid #fef3c7', borderRadius: 16, padding: '16px', marginBottom: 12 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#d97706', fontWeight: 700, fontSize: 13, marginBottom: 8 }}>
                                        <Warning style={{ fontSize: 14 }} /> <span>成长弱势与健康预警</span>
                                    </div>
                                    <ul style={{ margin: 0, paddingLeft: 16, color: '#b45309', fontSize: 12, lineHeight: 1.6 }}>
                                        {latest.warnings.map((warn, index) => (
                                            <li key={index} style={{ marginBottom: 4 }}>{warn}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* AI Expert Suggestions */}
                            {latest.ai_advice && (
                                <div style={{
                                    background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
                                    border: '1px solid #ddd6fe',
                                    borderRadius: 16,
                                    padding: '18px 16px',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    marginBottom: 16
                                }}>
                                    <div style={{ position: 'absolute', top: -15, right: -15, fontSize: 60, opacity: 0.08 }}>🤖</div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#7c3aed', fontWeight: 800, fontSize: 14, marginBottom: 8 }}>
                                        <span>✨</span> AI 智能成长及运动处方
                                    </div>
                                    <div style={{ fontSize: 12.5, color: '#6d28d9', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                                        {latest.ai_advice}
                                    </div>
                                </div>
                            )}

                            {/* Exemption Card (If Exemption Active) */}
                            {latest.exemption && (
                                <div style={{ background: '#ecfdf5', border: '1px solid #d1fae5', borderRadius: 16, padding: '16px', marginBottom: 16 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                        <span style={{ fontSize: 13, fontWeight: 700, color: '#065f46' }}>免测申请审核通过</span>
                                        <Tag type="success" style={{ borderRadius: 6 }}>{latest.exemption.academic_year}</Tag>
                                    </div>
                                    <div style={{ fontSize: 12, color: '#047857', lineHeight: 1.5 }}>
                                        <strong>免测事由：</strong>{latest.exemption.reason}
                                    </div>
                                    {latest.exemption.audit_opinion && (
                                        <div style={{ fontSize: 11, color: '#059669', background: '#fff', padding: 8, borderRadius: 8, marginTop: 8 }}>
                                            审核批复：{latest.exemption.audit_opinion} ({latest.exemption.auditor_name})
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}

                    {/* TAB 2: Trends Charts */}
                    {activeTab === 'trends' && (
                        <>
                            {renderTrendChart('历年身体质量指数 (BMI) 趋势', trends.height_weight, 'bmi', 'academic_year', '#fa8c16')}
                            {renderTrendChart('历年体测总评得分变化趋势', trends.scores, 'composite_score', 'academic_year', '#00b578')}
                        </>
                    )}

                    {/* TAB 3: Item Timelines */}
                    {activeTab === 'items' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            {trends.items && Object.entries(trends.items).map(([key, list]) => {
                                if (!list || list.length === 0) return null;
                                
                                const labelMap = {
                                    vital_capacity: '肺活量机能 (ml)',
                                    run_50m: '50米速度跑 (s)',
                                    standing_long_jump: '立定爆发力 (cm)',
                                    skip_rope: '一分钟双足跳绳 (次)',
                                    sit_and_reach: '坐位柔韧体前屈 (cm)',
                                    sit_up: '仰卧起坐核心耐力 (次)',
                                    pull_up: '引体向上上肢力量 (次)',
                                    run_800m_1000m: '耐力跑(男生1000m/女生800m)'
                                };

                                return (
                                    <div key={key} style={{ background: '#fff', borderRadius: 16, padding: '16px' }}>
                                        <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 16, borderBottom: '1px solid #f1f5f9', paddingBottom: 10 }}>
                                            {labelMap[key] || key}
                                        </div>
                                        {renderTimeline(list)}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            ) : (
                <Empty description="未能生成该学生的健康档案，请录入体测成绩后再试。" />
            )}

            {/* Child Switcher Picker */}
            <ActionSheet
                visible={showChildPicker}
                actions={pickerActions}
                onCancel={() => setShowChildPicker(false)}
                onSelect={handleSelectChild}
                cancelText="取消"
                description="请选择学生"
            />
        </div>
    );
}
